import { EPHEMERAL_ATTRS } from 'client-portal/models/card';
import { VALID_PAID_PAYMENT_METHODS } from 'client-portal/utils/prescreener/constants';
import { callbackError } from 'client-portal/utils/error-handling';
import { createElement, error } from 'client-portal/utils/banners';
import { recentlyBookedSlotFromReservation } from 'client-portal/utils/recently-booked-slot';
import { service } from '@ember/service';
import { task } from 'ember-concurrency';
import Mixin from '@ember/object/mixin';
import withSnakeCaseKeys from 'client-portal/utils/with-snake-case-keys';

// eslint-disable-next-line ember/no-new-mixins
export default Mixin.create({
  iframe: service(),
  pendingAppointment: service(),
  currentPractice: service(),
  analytics: service(),
  clientPortalTracking: service(),
  consentManager: service(),
  store: service(),
  session: service(),
  prescreener: service(),
  signOut: service(),
  intl: service(),
  bookingResume: service(),

  getAppointment() {
    return this.getInformationModel().appointment;
  },

  getInformationModel() {
    let { pendingAppointment } = this;
    if (!pendingAppointment.appointment) {
      let reservation = this.modelFor(this.request.baseRouteName);
      let clientParams = this.paramsFor('-request');
      pendingAppointment.updatePendingAppointmentData({ reservation, clientParams });
    }
    return pendingAppointment.informationModel;
  },

  errorContent(message) {
    let title, content;

    if (message.title === 'duplicate-couple-email') {
      title = this.intl.t('errors.appointment.duplicateEmail.title');
      content = this.intl.t('errors.appointment.duplicateEmail.coupleMessage');
    } else if (message.title === 'duplicate-minor-email') {
      title = this.intl.t('errors.appointment.duplicateEmail.title');
      content = this.intl.t('errors.appointment.duplicateEmail.minorMessage');
    } else {
      title = this.intl.t('errors.appointment.unableToRequest.title');
      content = message.title;
    }

    content = createElement('p', { innerHTML: content });

    return { title, content };
  },

  validateAndPersistAppointmentTask: task(function* (stripeElements) {
    try {
      yield this.validateFieldsTask.perform(stripeElements);
      yield this.persistAppointmentTask.perform();
    } catch (err) {
      if (err) console.error(err); // eslint-disable-line no-console
      // show validation messages
      // persistAppointmentTask handles its own errors
    }
  }),

  validatePrescreenerTask: task(function* () {
    if (!this.prescreener.validation.validateAll()) {
      return Promise.reject();
    }
  }),

  validateFieldsTask: task(function* (stripeElements) {
    let { card, client } = this.getAppointment();

    client.set('_isDirty', true);
    yield client.validate();

    if (this.prescreener.isPrescreenerEnabled) {
      yield this.validatePrescreenerTask.perform();
    }

    if (client.validations.isInvalid) {
      return Promise.reject();
    }

    if (card) {
      card.set('_isDirty', true);
      const isCardValid = yield this._handleCreditCardCreation(card, stripeElements);
      if (!isCardValid) {
        return Promise.reject();
      }
    }
  }),

  createCardTokenTask: task(function* (card) {
    try {
      yield card.createToken();
      return true;
    } catch {
      return false;
    }
  }),

  async _sendWidgetInsuranceCardFile(clientId, file) {
    let { store } = this;
    return !(await store
      .createRecord('unauthenticated-insurance-card', {
        file,
        clientId,
      })
      .save());
  },

  persistAppointmentTask: task(function* () {
    let appointment = this.getAppointment();

    try {
      let { card, client } = appointment;
      let { insuranceCardFrontSideFile, insuranceCardBackSideFile } = client;

      let opts = {};
      let { isNew: isNewClient } = client;

      if (isNewClient) {
        opts = {
          adapterOptions: {
            query: { include: 'client,card,office' },
            embed: [
              'client',
              'card',
              'clientRelationships',
              'relatedClient',
              'emails',
              'phones',
              ...(this.prescreener.isPrescreenerEnabled ? ['prescreenerAnswer'] : []),
            ],
          },
        };

        this.prescreener.refreshAnswerRecord();
        this.prescreener.updateClientsForAnswers(client);
      }

      if (
        !this.prescreener.isPrescreenerEnabled &&
        (insuranceCardFrontSideFile || insuranceCardBackSideFile)
      ) {
        appointment.set(
          'files',
          [insuranceCardFrontSideFile, insuranceCardBackSideFile]
            .filter(Boolean)
            .map(uploadFile => uploadFile.file)
        );
      }

      appointment.set('datagrailConsentPreferences', this.consentManager.getConsentPreferences());

      yield appointment.save(opts);

      let relationship = client.clientRelationships.slice().pop();
      appointment.client.isMinor = client.isMinor;
      appointment.relatedClient = relationship?.relatedClient;
      if (isNewClient) {
        this.session.newProspectiveClient = appointment.client;
      }

      this.session.set('data.skipLicenseAgreement', isNewClient);
      this.session.set('data.skipLegalAgreements', isNewClient);

      // Re-attach data to a new card in order for validations to not
      // display invalid while we're transitioning
      if (card) {
        EPHEMERAL_ATTRS.forEach(x => appointment.card.set(x, card[x]));
      }

      let reservation = this.modelFor(this.request.baseRouteName);
      reservation.set('appointment', appointment);

      let { message, reasonsForVisit, mentalHealthHistory, careTypes } = reservation;
      let clientMessageSent = !!message;
      let { id: appointmentId, insurancePaymentMethod } = appointment;
      let hasReasonsForVisit = !!reasonsForVisit?.length;
      let hasMentalHealthHistory = !!mentalHealthHistory?.length;
      let hasPaymentMethod = VALID_PAID_PAYMENT_METHODS.includes(insurancePaymentMethod);
      let isPaymentInsurance = insurancePaymentMethod === 'Insurance';

      let appointmentRequestAnswerData = this.prescreener.appointmentRequestAnswerData;

      let paymentAndInsurance = {
        'payment_question': hasPaymentMethod,
        isPaymentInsurance,
        ...(isPaymentInsurance &&
          withSnakeCaseKeys({
            insuranceFrontUpload: !!insuranceCardFrontSideFile,
            insuranceBackUpload: !!insuranceCardBackSideFile,
          })),
      };
      let mergedPaymentAndInsurance = { ...paymentAndInsurance, ...appointmentRequestAnswerData };

      let hasCareTypes = !!careTypes?.length;

      this.iframe.appointmentRequested({
        clientMessageSent,
        appointmentId,
        reasonForVisitMessage: clientMessageSent,
        hasReasonsForVisit,
        hasMentalHealthHistory,
        hasCareTypes,
        hasPaymentMethod,
        ...mergedPaymentAndInsurance,
      });
      this.recordSiteBookedSlot();
    } catch (err) {
      let errorHandled = yield this.isPrescreenerBadSignoutError(err);

      let showErrorMessage =
        !appointment.card ||
        appointment.card.validations.isValid ||
        (this.session.canUsePaymentMethods && appointment.card.paymentMethodId);

      if (!appointment.client.isNew) {
        this.request.transitionTo(this, 'date');
      }

      if (showErrorMessage && !errorHandled) {
        callbackError(err, message => {
          let errorContent = this.errorContent(message);
          this.clientPortalTracking.trackErrorOnAppointmentsPage(errorContent.title);
          error(errorContent);
          return true;
        });
      }
      return;
    }

    yield this.transitionToConfirmationThenRefresh();
  }).drop(),

  recordSiteBookedSlot() {
    if (this.request.baseRouteName !== 'site.request') return;

    let slot = recentlyBookedSlotFromReservation(this.modelFor(this.request.baseRouteName));
    if (!slot) return;

    this.bookingResume.setRecentlyBookedSlot(slot);
  },

  async transitionToConfirmationThenRefresh() {
    try {
      await this.request.transitionTo(this, 'confirmation', {
        queryParams: {
          selectedSlot: null,
          currentDate: null,
          currentTimeZone: null,
        },
      });
    } catch (err) {
      if (err.message !== 'TransitionAborted') {
        window._bugsnagClient?.notify(err);
      }
    } finally {
      this.send('refreshEnvironment');
    }
  },

  setupController(controller) {
    this._super(...arguments);
    controller.setProperties({
      validateFieldsTask: this.validateFieldsTask,
      persistAppointmentTask: this.persistAppointmentTask,
      validatePrescreenerTask: this.validatePrescreenerTask,
      validateAndPersistAppointmentTask: this.validateAndPersistAppointmentTask,
    });
  },

  async isPrescreenerBadSignoutError(err) {
    if (this.prescreener.isPrescreenerEnabled && !this.triedSiginingOut) {
      // sometimes the prescreener will get in the way of creating appointments
      // because a user looks previously signed in. If this occurs the client
      // will be signed out and another attempt will be made to set the appointment
      // this happens because a previous appointment creation was not properly
      // signed out and the session is still valid for another user

      let errors = err.errors ?? [];
      let signOutProgress = this.trySigningOut(errors);

      return await this.hasTriedSigningOut(signOutProgress);
    }

    return false;
  },

  trySigningOut(errors) {
    const UNAUTHORIZED = '403';
    const PRESCREENER_BAD_SIGNOUT = 'You have no access to this client/clients';

    return errors
      .map(error => {
        if (
          error.code === UNAUTHORIZED &&
          error.title === PRESCREENER_BAD_SIGNOUT &&
          !this.triedSiginingOut
        ) {
          this.triedSiginingOut = true;
          return this.signOut.signOutSilently(this.persistAppointmentTask.perform);
        }
      })
      .filter(Boolean);
  },

  async hasTriedSigningOut(signOutProgress) {
    if (this.triedSiginingOut) {
      await Promise.allSettled(signOutProgress);
      return true;
    }
  },

  async _handleCreditCardCreation(card, stripeElements) {
    if (this.session.canUsePaymentMethods) {
      return await this._handleStripePaymentMethodFlow(card, stripeElements);
    } else {
      return await this._handleStripeCardTokenFlow(card);
    }
  },

  async _handleStripePaymentMethodFlow(card, stripeElements) {
    if (card.paymentMethodId) {
      return true;
    }

    const { error } = await stripeElements.submit();
    if (error || !card.name) {
      return false;
    }

    return !!(await this.stripeCards.addPaymentMethodTask.perform({
      elements: stripeElements,
      newCard: card,
      persistCard: false,
    }));
  },

  async _handleStripeCardTokenFlow(card) {
    if (card.customStripeCardId) {
      return true;
    }

    await card.validate();
    const isCardValid = card.validations.isValid;

    return isCardValid && (await this.createCardTokenTask.perform(card));
  },
});
